/**
 * Suitsupply-style multi-product-type size picker
 */
class SuitsupplySizePicker extends HTMLElement {
  static JACKET_MATRIX = [
    { alpha: 'XXS', regular: '42', long: null, short: null },
    { alpha: 'XS', regular: '44', long: null, short: '22' },
    { alpha: 'S', regular: '46', long: '90', short: '24' },
    { alpha: 'M', regular: '48', long: '94', short: '26' },
    { alpha: 'M/L', regular: '50', long: '98', short: '28' },
    { alpha: 'L', regular: '52', long: '102', short: '29' },
    { alpha: 'XL', regular: '54', long: '106', short: '30' },
    { alpha: 'XXL', regular: '56', long: '110', short: null },
    { alpha: '3XL', regular: '58', long: '114', short: null },
    { alpha: '4XL', regular: '60', long: '118', short: null },
  ];

  static COMBO_WAISTS = ['28', '30', '32', '34', '36', '38', '40'];
  static COMBO_INSEAMS = ['28', '30', '32', '34', '36'];

  static TROUSER_WAISTS = ['28', '30', '32', '34', '36', '38', '40', '42', '44', '46', '48', '50', '52', '54', '56', '58', '60'];

  static MODE_CONFIG = {
    'suit-mix': { mix: true, required: ['jacketSize', 'jacketLength', 'trouserSize', 'trouserLength'] },
    suit: { panel: 'jacket-matrix', required: ['jacketSize', 'jacketLength'], bundled: true },
    jacket: { panel: 'jacket-matrix', required: ['jacketSize', 'jacketLength'] },
    coat: { panel: 'jacket-matrix', required: ['jacketSize', 'jacketLength'] },
    waistcoat: { panel: 'jacket-matrix', required: ['jacketSize', 'jacketLength'] },
    trouser: { panel: 'trouser-full', required: ['trouserSize', 'inseam'] },
    'trouser-suit': { panel: 'trouser-waist', required: ['trouserSize', 'trouserLength'] },
    'trouser-casual': { panel: 'waist-inseam-combo', required: ['trouserSize', 'inseam'] },
    shirt: { panel: 'shirt', required: ['shirtSize'] },
    knitwear: { panel: 'alpha', required: ['alphaSize'] },
    shoes: { panel: 'shoe', required: ['shoeSize'] },
  };

  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.mode = this.dataset.mode || 'suit-mix';
    this.config = { ...(SuitsupplySizePicker.MODE_CONFIG[this.mode] || SuitsupplySizePicker.MODE_CONFIG['suit-mix']) };
    this.applyFeatureFlags();
    this.productFormId = this.dataset.productFormId || '';
    this.buttonLabel = this.dataset.buttonLabel || 'Select size';
    this.jacketBtnLabel = this.dataset.jacketLabel || 'Select size';
    this.trouserBtnLabel = this.dataset.trouserLabel || 'Select size';

    this.sizeOverlay = this.querySelector('[data-overlay="size"]');
    this.chartOverlay = this.querySelector('[data-overlay="chart"]');
    this.portalOverlays();

    this.state = {
      jacketSize: '',
      jacketLength: '',
      trouserSize: '',
      trouserLength: '',
      inseam: '',
      shirtSize: '',
      alphaSize: '',
      shoeSize: '',
      step: 0,
      activePanel: this.config.panel || (this.config.flow ? this.config.flow[0] : 'jacket-matrix'),
    };

    this.renderMatrix();
    this.renderWaistMatrix();
    this.renderComboGrid();
    this.bindEvents();
    this.setupValidation();
    this.syncTrigger();
    this.updateFooter();
    requestAnimationFrame(() => {
      this.ensureChartReady(() => {
        if (typeof window.initSsSizeCharts === 'function') {
          window.initSsSizeCharts(this.chartOverlay);
        }
        if (typeof window.initSsMeasureGuides === 'function') {
          window.initSsMeasureGuides(this.chartOverlay);
        }
      });
    });
  }

  portalOverlays() {
    const blockClass = this.dataset.blockClass || '';
    [this.sizeOverlay, this.chartOverlay].forEach((overlay) => {
      if (!overlay) return;
      if (blockClass) overlay.classList.add(blockClass);
      if (overlay.parentElement !== document.body) {
        document.body.appendChild(overlay);
      }
    });
  }

  jacketEnabled() {
    return this.dataset.enableJacketChartFit !== 'false';
  }

  trouserEnabled() {
    return this.dataset.enableTrouserChartFit !== 'false';
  }

  applyFeatureFlags() {
    if (!this.config.mix) return;

    const required = [];
    if (this.jacketEnabled()) {
      required.push('jacketSize', 'jacketLength');
    }
    if (this.trouserEnabled()) {
      required.push('trouserSize', 'trouserLength');
    }
    this.config = { ...this.config, required };
  }

  inSizeModal(selector) {
    return this.sizeOverlay?.querySelector(selector) ?? null;
  }

  renderMatrix() {
    this.renderLengthMatrix({
      body: this.inSizeModal('[data-matrix-body]'),
      showAlpha: true,
      isSelected: (value, length) =>
        this.state.jacketSize === value && this.state.jacketLength === this.lengthLabel(length),
      sizeAttr: 'size',
    });
  }

  renderWaistMatrix() {
    this.renderLengthMatrix({
      body: this.inSizeModal('[data-waist-matrix-body]'),
      showAlpha: true,
      isSelected: (value, length) =>
        this.state.trouserSize === value && this.state.trouserLength === this.lengthLabel(length),
      sizeAttr: 'waist-size',
    });
  }

  renderLengthMatrix({ body, showAlpha, isSelected, sizeAttr }) {
    if (!body) return;

    const lengths = ['regular', 'long', 'short'];
    const alphaCol = showAlpha
      ? SuitsupplySizePicker.JACKET_MATRIX.map(
          (row) => `<span class="ss-size-picker__alpha">${row.alpha}</span>`
        ).join('')
      : '';

    const columns = lengths
      .map((length) => {
        const cells = SuitsupplySizePicker.JACKET_MATRIX.map((row) => {
          const value = row[length];
          if (!value) {
            return '<span class="ss-size-picker__matrix-spacer" aria-hidden="true"></span>';
          }
          const selected = isSelected(value, length) ? ' ss-size-picker__size-btn--selected' : '';
          return `<button type="button" class="ss-size-picker__size-btn${selected}" data-${sizeAttr}="${value}" data-length="${length}">${value}</button>`;
        }).join('');
        return `<div class="ss-size-picker__matrix-col" data-length="${length}">${cells}</div>`;
      })
      .join('');

    body.innerHTML = showAlpha
      ? `<div class="ss-size-picker__matrix-alpha-col">${alphaCol}</div>${columns}`
      : columns;
  }

  renderComboGrid() {
    const grid = this.inSizeModal('[data-combo-grid]');
    if (!grid) return;
    grid.innerHTML = SuitsupplySizePicker.COMBO_WAISTS.flatMap((waist) =>
      SuitsupplySizePicker.COMBO_INSEAMS.map((inseam) => {
        const selected =
          this.state.trouserSize === waist && this.state.inseam === inseam
            ? ' ss-size-picker__size-btn--selected'
            : '';
        return `<button type="button" class="ss-size-picker__size-btn${selected}" data-combo-waist="${waist}" data-combo-inseam="${inseam}">${waist}/${inseam}</button>`;
      })
    ).join('');
  }

  lengthLabel(key) {
    return { regular: 'Regular', long: 'Long', short: 'Short' }[key] || key;
  }

  bindEvents() {
    this.querySelector('[data-open-size="auto"]')?.addEventListener('click', () => this.openForMode());

    this.querySelectorAll('[data-open-size="jacket"], [data-open-size="trouser"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panel = btn.dataset.openSize === 'jacket' ? 'jacket-matrix' : 'trouser-waist';
        this.openPanel(panel);
      });
    });

    this.inSizeModal('[data-close-size]')?.addEventListener('click', () => this.closeModal('size'));
    this.inSizeModal('[data-back]')?.addEventListener('click', () => this.goBack());

    this.bindChartTriggers(this, false);
    this.bindChartTriggers(this.sizeOverlay, true);

    this.chartOverlay?.addEventListener('click', (e) => {
      if (e.target.closest('[data-back-chart]')) {
        e.preventDefault();
        this.closeChartBack();
      } else if (e.target.closest('[data-close-chart]')) {
        e.preventDefault();
        this.closeChart();
      }
    });

    this.chartOverlay?.addEventListener('ss-measure-guide:opened', () => {
      this.chartOverlay?.querySelector('.ss-size-picker__header--chart')?.setAttribute('hidden', '');
      this.chartOverlay?.querySelector('.ss-size-picker__chart-body')?.setAttribute('aria-hidden', 'true');
    });

    this.chartOverlay?.addEventListener('ss-measure-guide:closed', () => {
      this.chartOverlay?.querySelector('.ss-size-picker__header--chart')?.removeAttribute('hidden');
      this.chartOverlay?.querySelector('.ss-size-picker__chart-body')?.removeAttribute('aria-hidden');
    });

    this.chartOverlay?.addEventListener('ss-measure-guide:close-all', () => {
      this.closeChart();
    });

    this.inSizeModal('[data-matrix-body]')?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-size]');
      if (!btn) return;
      this.state.jacketSize = btn.dataset.size;
      this.state.jacketLength = this.lengthLabel(btn.dataset.length);
      if (this.config.bundled) {
        this.state.trouserSize = btn.dataset.size;
        this.state.trouserLength = this.state.jacketLength;
      }
      this.renderMatrix();
      this.renderWaistMatrix();
      this.syncHiddenInputs();
      this.updateFooter();
    });

    this.inSizeModal('[data-waist-matrix-body]')?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-waist-size]');
      if (!btn) return;
      this.state.trouserSize = btn.dataset.waistSize;
      this.state.trouserLength = this.lengthLabel(btn.dataset.length);
      this.renderWaistMatrix();
      this.syncHiddenInputs();
      this.updateFooter();
    });

    this.wireGrid('trouser-size', 'trouserSize');
    this.wireGrid('inseam', 'inseam');
    this.wireGrid('shirt-size', 'shirtSize');
    this.wireGrid('alpha-size', 'alphaSize');
    this.wireGrid('shoe-size', 'shoeSize');

    this.inSizeModal('[data-combo-grid]')?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-combo-waist]');
      if (!btn) return;
      this.state.trouserSize = btn.dataset.comboWaist;
      this.state.inseam = btn.dataset.comboInseam;
      this.renderComboGrid();
      this.updateFooter();
    });

    this.inSizeModal('[data-modal-action]')?.addEventListener('click', () => this.handleAction());

    this._onKeydown = (e) => {
      if (e.key === 'Escape') {
        if (this.chartOverlay?.classList.contains('active')) {
          if (typeof window.closeSsMeasureGuide === 'function') {
            const modal = this.chartOverlay.querySelector('.ss-size-picker__modal--chart');
            if (modal?.classList.contains('ss-measure-guide-open')) {
              window.closeSsMeasureGuide(modal);
              return;
            }
          }
          this.closeChart();
        } else {
          this.closeModal('size');
        }
      }
    };
    document.addEventListener('keydown', this._onKeydown);
  }

  wireGrid(dataAttr, stateKey) {
    this.sizeOverlay?.addEventListener('click', (e) => {
      const btn = e.target.closest(`[data-${dataAttr}]`);
      if (!btn) return;
      const camel = dataAttr.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      this.state[stateKey] = btn.dataset[camel];
      this.sizeOverlay.querySelectorAll(`[data-${dataAttr}]`).forEach((el) => {
        el.classList.toggle('ss-size-picker__size-btn--selected', el === btn);
      });
      this.updateFooter();
    });
  }

  openForMode() {
    if (this.config.flow) {
      this.state.step = 0;
      this.openPanel(this.config.flow[0]);
    } else if (this.config.panel) {
      this.openPanel(this.config.panel);
    }
  }

  openPanel(panel) {
    this.state.activePanel = panel;
    this.sizeOverlay?.querySelectorAll('[data-panel]').forEach((el) => {
      el.classList.toggle('hidden', el.dataset.panel !== panel);
    });
    const titles = {
      'jacket-matrix': 'Select your size',
      'trouser-full': 'Select trouser size',
      'trouser-waist': 'Select waist size',
      'waist-inseam-combo': 'Select size',
      shirt: 'Select shirt size',
      alpha: 'Select size',
      shoe: 'Select shoe size',
    };
    const title = this.inSizeModal('[data-modal-title]');
    if (title) title.textContent = titles[panel] || 'Select your size';
    const back = this.inSizeModal('[data-back]');
    if (back) back.style.visibility = this.config.flow && this.state.step > 0 ? 'visible' : 'hidden';
    if (panel === 'trouser-waist') this.renderWaistMatrix();
    this.updateFooter();
    this.openModal('size');
  }

  openModal(name) {
    const overlay = name === 'chart' ? this.chartOverlay : this.sizeOverlay;
    if (!overlay) return;
    overlay.classList.add('active');
    overlay.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ss-size-picker--lock');
  }

  closeModal(name) {
    const overlay = name === 'chart' ? this.chartOverlay : this.sizeOverlay;
    if (!overlay) return;
    overlay.classList.remove('active');
    overlay.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.ss-size-picker__overlay.active')) {
      document.documentElement.classList.remove('ss-size-picker--lock');
    }
  }

  goBack() {
    if (this.config.flow && this.state.step > 0) {
      this.state.step -= 1;
      this.openPanel(this.config.flow[this.state.step]);
    } else {
      this.closeModal('size');
    }
  }

  handleAction() {
    const panel = this.config.flow ? this.config.flow[this.state.step] : this.state.activePanel;
    if (!this.panelComplete(panel)) return;

    if (this.config.mix) {
      this.syncHiddenInputs();
      this.syncTrigger();
      this.closeModal('size');
      return;
    }

    if (this.config.flow && this.state.step < this.config.flow.length - 1) {
      this.state.step += 1;
      this.openPanel(this.config.flow[this.state.step]);
      return;
    }

    if (!this.allSizesSelected()) return;
    this.syncHiddenInputs();
    this.syncTrigger();
    this.closeModal('size');

    const form = document.getElementById(this.productFormId);
    const submitBtn = form?.querySelector('[ref="addToCartButton"], button[name="add"], button[type="submit"]');
    if (submitBtn && !submitBtn.disabled) submitBtn.click();
  }

  panelComplete(panel) {
    switch (panel) {
      case 'jacket-matrix':
        return !!(this.state.jacketSize && this.state.jacketLength);
      case 'trouser-full':
        return !!(this.state.trouserSize && this.state.inseam);
      case 'trouser-waist':
        return !!(this.state.trouserSize && this.state.trouserLength);
      case 'waist-inseam-combo':
        return !!(this.state.trouserSize && this.state.inseam);
      case 'shirt':
        return !!this.state.shirtSize;
      case 'alpha':
        return !!this.state.alphaSize;
      case 'shoe':
        return !!this.state.shoeSize;
      default:
        return false;
    }
  }

  syncHiddenInputs() {
    this.setProp('jacket-size', this.state.jacketSize);
    this.setProp('jacket-length', this.state.jacketLength);
    this.setProp('trouser-size', this.state.trouserSize);
    this.setProp('inseam', this.state.inseam);
    this.setProp('shirt-size', this.state.shirtSize);
    this.setProp('alpha-size', this.state.alphaSize);
    this.setProp('shoe-size', this.state.shoeSize);
  }

  setProp(name, value) {
    const input = this.querySelector(`[data-property="${name}"]`);
    if (input) input.value = value || '';
  }

  syncTrigger() {
    if (this.config.mix) {
      const jacketDisplay = this.querySelector('[data-mix-display="jacket"]');
      const trouserDisplay = this.querySelector('[data-mix-display="trouser"]');
      if (jacketDisplay && this.jacketEnabled()) {
        jacketDisplay.textContent =
          this.state.jacketSize && this.state.jacketLength
            ? `US ${this.state.jacketSize} ${this.state.jacketLength}`
            : this.jacketBtnLabel;
      }
      if (trouserDisplay && this.trouserEnabled()) {
        trouserDisplay.textContent =
          this.state.trouserSize && this.state.trouserLength
            ? `US ${this.state.trouserSize} ${this.state.trouserLength}`
            : this.trouserBtnLabel;
      }
      this.updateCheckoutState();
      return;
    }

    const trigger = this.querySelector('[data-open-size="auto"]');
    const label = trigger?.querySelector('[data-trigger-label]');
    if (!label || !trigger) return;

    if (this.allSizesSelected()) {
      label.textContent = this.formatSelectionLabel();
      trigger.classList.add('ss-size-picker__trigger--selected');
    } else {
      label.textContent = this.buttonLabel;
      trigger.classList.remove('ss-size-picker__trigger--selected');
    }
  }

  formatSelectionLabel() {
    if (this.mode === 'shirt') return `Collar ${this.state.shirtSize}`;
    if (this.mode === 'knitwear') return this.state.alphaSize;
    if (this.mode === 'shoes') return `EU ${this.state.shoeSize}`;
    if (this.mode === 'trouser-casual') return `${this.state.trouserSize}/${this.state.inseam}`;
    if (this.mode === 'trouser-suit') {
      return this.state.trouserLength
        ? `US ${this.state.trouserSize} ${this.state.trouserLength}`
        : `US ${this.state.trouserSize}`;
    }
    if (this.mode === 'trouser') return `${this.state.trouserSize} / ${this.state.inseam}`;
    if (this.mode === 'suit') {
      return `US ${this.state.jacketSize} ${this.state.jacketLength}`;
    }
    return `US ${this.state.jacketSize} ${this.state.jacketLength}`;
  }

  bindChartTriggers(container, fromSizeModal) {
    if (!container || container._ssChartTriggersBound) return;
    container._ssChartTriggersBound = true;
    container.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-open-chart]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const guide =
        btn.dataset.chartGuide === 'auto' || !btn.dataset.chartGuide
          ? this.defaultChartGuide()
          : btn.dataset.chartGuide;
      this.openChartGuide(guide, fromSizeModal);
    });
  }

  resolveChartGuide(guide) {
    if (!guide || guide === 'auto') return this.defaultChartGuide();
    return guide;
  }

  updateChartHeader() {
    const back = this.chartOverlay?.querySelector('[data-back-chart]');
    if (back) back.hidden = !this._chartFromSizeModal;
  }

  ensureChartReady(callback) {
    const run = (attempt = 0) => {
      if (typeof initSsSizeCharts === 'function' && typeof SS_SIZE_CHART_FITS !== 'undefined') {
        callback();
        return;
      }
      if (attempt < 40) {
        setTimeout(() => run(attempt + 1), 50);
      }
    };
    run();
  }

  closeChart() {
    if (typeof window.closeSsMeasureGuide === 'function') {
      window.closeSsMeasureGuide(this.chartOverlay?.querySelector('.ss-size-picker__modal--chart'));
    }
    this.closeModal('chart');
    this._chartFromSizeModal = false;
    this.updateChartHeader();
  }

  closeChartBack() {
    this.closeModal('chart');
    this._chartFromSizeModal = false;
    this.updateChartHeader();
  }

  defaultChartGuide() {
    const map = {
      'suit-mix': 'jacket',
      suit: 'jacket',
      jacket: 'jacket',
      coat: 'jacket',
      waistcoat: 'jacket',
      trouser: 'trouser',
      'trouser-suit': 'trouser',
      'trouser-casual': 'trouser',
      shirt: 'shirt',
      knitwear: 'knitwear',
      shoes: 'shoes',
    };
    return map[this.mode] || 'jacket';
  }

  openChartGuide(guide, fromSizeModal = false) {
    this._chartFromSizeModal = fromSizeModal;
    this.updateChartHeader();
    const resolved = this.resolveChartGuide(guide);
    this.prepareChartFocus(resolved);
    this.openModal('chart');
    this.ensureChartReady(() => {
      if (typeof window.initSsSizeCharts === 'function') {
        window.initSsSizeCharts(this.chartOverlay);
      }
      if (typeof window.initSsMeasureGuides === 'function') {
        window.initSsMeasureGuides(this.chartOverlay);
      }
      this.scrollChartToSection(resolved);
    });
  }

  prepareChartFocus(guide) {
    const chartEl = this.chartOverlay?.querySelector('[data-ss-chart]');
    if (!chartEl) return;

    const type = chartEl.dataset.chartType;
    const jacketEnabled = this.dataset.enableJacketChartFit !== 'false';
    const trouserEnabled = this.dataset.enableTrouserChartFit !== 'false';

    if (!this._chartFromSizeModal) {
      chartEl.dataset.showJacket = jacketEnabled ? chartEl.dataset.showJacketDefault || 'true' : 'false';
      chartEl.dataset.showTrousers = trouserEnabled ? chartEl.dataset.showTrousersDefault || 'true' : 'false';
      return;
    }

    const showBoth = type === 'suit' && !this.config.mix;

    if (guide === 'trouser') {
      chartEl.dataset.showJacket = 'false';
      chartEl.dataset.showTrousers = trouserEnabled ? 'true' : 'false';
    } else if (guide === 'jacket' && this.config.mix) {
      chartEl.dataset.showJacket = jacketEnabled ? 'true' : 'false';
      chartEl.dataset.showTrousers = 'false';
    } else if (showBoth) {
      chartEl.dataset.showJacket = jacketEnabled ? 'true' : 'false';
      chartEl.dataset.showTrousers = trouserEnabled ? 'true' : 'false';
    } else {
      chartEl.dataset.showJacket = jacketEnabled
        ? chartEl.dataset.showJacketDefault || chartEl.dataset.showJacket
        : 'false';
      chartEl.dataset.showTrousers = trouserEnabled
        ? chartEl.dataset.showTrousersDefault || chartEl.dataset.showTrousers
        : 'false';
    }
  }

  scrollChartToSection(guide) {
    const body = this.chartOverlay?.querySelector('.ss-size-picker__chart-body');
    if (!body) return;
    body.scrollTop = 0;
    if (guide === 'trouser' || guide === 'trouser-suit') {
      const section = this.chartOverlay?.querySelector('[data-chart-section="trouser"]');
      section?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }

  updateFooter() {
    const btn = this.inSizeModal('[data-modal-action]');
    if (btn) {
      const panel = this.config.flow ? this.config.flow[this.state.step] : this.state.activePanel;
      const complete = this.panelComplete(panel);

      if (this.config.mix) {
        btn.textContent = 'Apply';
      } else if (this.config.flow && this.state.step < this.config.flow.length - 1) {
        btn.textContent = 'Continue';
      } else {
        btn.textContent = 'Add to bag';
      }
      btn.disabled = !complete;
    }
    this.updateCheckoutState();
  }

  productFormScope() {
    const form = this.productFormId ? document.getElementById(this.productFormId) : null;
    return form?.closest('product-form-component') || form || null;
  }

  updateCheckoutState() {
    const selected = this.allSizesSelected();
    const scope = this.productFormScope() || document;

    scope.querySelectorAll('add-to-cart-component').forEach((component) => {
      if (selected && typeof component.enable === 'function') {
        component.enable();
      } else if (!selected && typeof component.disable === 'function') {
        component.disable();
      }
    });

    scope.querySelectorAll(
      `#${this.productFormId} button[type="submit"],
       #${this.productFormId} button[name="add"],
       button.add-to-cart-button,
       [ref="addToCartButton"]`
    ).forEach((btn) => {
      btn.disabled = !selected;
      btn.setAttribute('aria-disabled', selected ? 'false' : 'true');
      btn.classList.toggle('ss-size-picker__cart-btn--locked', !selected);
    });

    const paymentSelector =
      '.shopify-payment-button, .shopify-payment-button__button, .shopify-payment-button button, .accelerated-checkout-block, [ref="acceleratedCheckoutButtonContainer"], shopify-accelerated-checkout, shopify-buy-it-now-button, [data-testid="Checkout-button"], [name="goto_pp"], [name="goto_gc"], button.shopify-payment-button__button';

    scope.querySelectorAll(paymentSelector).forEach((el) => {
      if (el.matches('shopify-accelerated-checkout, shopify-buy-it-now-button, .accelerated-checkout-block, .shopify-payment-button')) {
        el.classList.toggle('ss-size-picker__payment-disabled', !selected);
        el.setAttribute('aria-disabled', selected ? 'false' : 'true');
        return;
      }
      if (selected) {
        el.removeAttribute('disabled');
        el.style.pointerEvents = '';
        el.style.opacity = '';
        el.setAttribute('aria-disabled', 'false');
      } else {
        el.setAttribute('disabled', 'true');
        el.style.pointerEvents = 'none';
        el.style.opacity = '0.5';
        el.setAttribute('aria-disabled', 'true');
      }
    });

    const form = this.productFormId ? document.getElementById(this.productFormId) : null;
    form?.classList.toggle('ss-size-picker__form--incomplete', !selected);
    this.classList.toggle('ss-size-picker--ready', selected);
  }

  allSizesSelected() {
    const req = this.config.required || [];
    if (this.config.mix && !req.length) return false;
    if (!req.length) return true;
    const map = {
      jacketSize: this.state.jacketSize,
      jacketLength: this.state.jacketLength,
      trouserSize: this.state.trouserSize,
      trouserLength: this.state.trouserLength,
      inseam: this.state.inseam,
      shirtSize: this.state.shirtSize,
      alphaSize: this.state.alphaSize,
      shoeSize: this.state.shoeSize,
    };
    return req.every((key) => !!map[key]);
  }

  setupValidation() {
    if (!this.productFormId) return;

    const paymentSelector =
      '.shopify-payment-button, .shopify-payment-button__button, .shopify-payment-button button, .accelerated-checkout-block, [ref="acceleratedCheckoutButtonContainer"], shopify-accelerated-checkout, shopify-buy-it-now-button, [data-testid="Checkout-button"], [name="goto_pp"], [name="goto_gc"], button.shopify-payment-button__button';

    const block = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (!this.config.mix) this.openForMode();
      return false;
    };

    const intercept = (e) => {
      if (this.allSizesSelected()) return;
      const addBtn = e.target.closest(
        `#${this.productFormId} button[type="submit"],
         #${this.productFormId} button[name="add"],
         [data-type="add-to-cart-form"] button[type="submit"],
         [data-type="add-to-cart-form"] button[name="add"],
         button.add-to-cart-button,
         [ref="addToCartButton"]`
      );
      if (addBtn) block(e);
      const paymentBtn = e.target.closest(paymentSelector);
      if (paymentBtn) block(e);
    };

    document.addEventListener(
      'submit',
      (e) => {
        if (e.target?.id === this.productFormId && !this.allSizesSelected()) block(e);
      },
      true
    );

    document.addEventListener('click', intercept, true);
    document.addEventListener('pointerdown', intercept, true);
    document.addEventListener('touchstart', intercept, true);
    document.addEventListener('keydown', (e) => {
      if (this.allSizesSelected()) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      intercept(e);
    }, true);

    this._checkoutObserver = new MutationObserver(() => this.updateCheckoutState());
    this._checkoutObserver.observe(document.body, { childList: true, subtree: true });

    this.updateCheckoutState();
  }

  disconnectedCallback() {
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    this._checkoutObserver?.disconnect();
  }
}

if (!customElements.get('suitsupply-size-picker')) {
  customElements.define('suitsupply-size-picker', SuitsupplySizePicker);
}
